import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { EdgeLinkBridgeDetail } from '@/lib/api/edgelink/types';
import { cn } from '@/lib/utils';
import { Check, Network } from 'lucide-react';
import CreateBridgeStep from './CreateBridgeStep';
import ConnectionInstructionsStep from './ConnectionInstructionsStep';
import VerificationStep from './VerificationStep';

type Step = 'create' | 'instructions' | 'verification';

interface ConnectEdgeLinkModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Callback when connection flow completes, returns the selected route ID */
  onComplete: (routeId: string) => void;
}

const STEPS: { key: Step; label: string }[] = [
  { key: 'create', label: 'Create' },
  { key: 'instructions', label: 'Connect' },
  { key: 'verification', label: 'Verify' },
];

export default function ConnectEdgeLinkModal({
  open,
  onOpenChange,
  onComplete,
}: ConnectEdgeLinkModalProps) {
  const [step, setStep] = useState<Step>('create');
  const [createdBridge, setCreatedBridge] = useState<EdgeLinkBridgeDetail | null>(null);

  const handleBridgeCreated = useCallback((bridge: EdgeLinkBridgeDetail) => {
    setCreatedBridge(bridge);
    setStep('instructions');
  }, []);

  const handleInstructionsContinue = useCallback(() => {
    setStep('verification');
  }, []);

  const handleVerificationComplete = useCallback(
    (routeId: string) => {
      onComplete(routeId);
      // Reset state for next time
      setStep('create');
      setCreatedBridge(null);
    },
    [onComplete]
  );

  const handleBack = useCallback(() => {
    if (step === 'instructions') {
      setStep('create');
    } else if (step === 'verification') {
      setStep('instructions');
    }
  }, [step]);

  const handleCancel = useCallback(() => {
    onOpenChange(false);
    // Reset state
    setStep('create');
    setCreatedBridge(null);
  }, [onOpenChange]);

  const handleSkip = useCallback(() => {
    if (createdBridge?.summary.routes?.[0]?.id) {
      onComplete(createdBridge.summary.routes[0].id);
    }
    // Reset state
    setStep('create');
    setCreatedBridge(null);
  }, [createdBridge, onComplete]);

  const currentStepIndex = STEPS.findIndex((s) => s.key === step);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] p-0 gap-0 overflow-hidden">
        {/* Header with step indicator */}
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-[#2E3835]">
          <div className="flex items-start gap-3 mb-2">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-muted/40 bg-muted/10">
              <Network className="h-5 w-5 text-lime" />
            </div>
            <div className="flex flex-col justify-center min-h-10">
              <DialogTitle className="text-lg">Add EdgeLink Bridge</DialogTitle>
              <DialogDescription className="text-sm mt-0.5 p-0">
                Create and connect a new EdgeLink bridge
              </DialogDescription>
            </div>
          </div>

          {/* Step indicator */}
          <div className="flex items-center gap-2 mt-4">
            {STEPS.map((s, index) => {
              const isActive = s.key === step;
              const isCompleted = index < currentStepIndex;

              return (
                <React.Fragment key={s.key}>
                  <div className="flex items-center gap-2">
                    <div
                      className={cn(
                        'flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold transition-colors',
                        isActive
                          ? 'bg-lime text-black'
                          : isCompleted
                            ? 'bg-lime/20 text-lime'
                            : 'bg-muted/20 text-muted-foreground'
                      )}
                    >
                      {isCompleted ? <Check className="h-3.5 w-3.5" /> : index + 1}
                    </div>
                    <span
                      className={cn(
                        'text-sm font-medium',
                        isActive ? 'text-white' : 'text-muted-foreground'
                      )}
                    >
                      {s.label}
                    </span>
                  </div>
                  {index < STEPS.length - 1 && (
                    <div
                      className={cn(
                        'flex-1 h-px',
                        index < currentStepIndex ? 'bg-lime/40' : 'bg-muted/20'
                      )}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </DialogHeader>

        {/* Step content */}
        <div className="flex-1">
          {step === 'create' && (
            <CreateBridgeStep
              onBridgeCreated={handleBridgeCreated}
              onCancel={handleCancel}
              existingBridge={createdBridge}
            />
          )}

          {step === 'instructions' && createdBridge && (
            <ConnectionInstructionsStep
              bridge={createdBridge}
              onContinue={handleInstructionsContinue}
              onBack={handleBack}
            />
          )}

          {step === 'verification' && createdBridge && (
            <VerificationStep
              bridgeId={createdBridge.summary.id}
              routeId={createdBridge.summary.routes?.[0]?.id || ''}
              onComplete={handleVerificationComplete}
              onSkip={handleSkip}
              onBack={handleBack}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
